import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, GripVertical, ArrowLeft, ArrowUp, ArrowDown, Star, FileX } from "lucide-react";

const QTYPES = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Long text" },
  { value: "url", label: "URL / Link" },
  { value: "select", label: "Multiple choice" },
  { value: "file", label: "File reference" },
];

const empty = {
  title: "", company: "", location: "", employment_type: "Full-time",
  department: "", is_volunteer: false,
  salary_range: "", description: "",
  responsibilities: [""], requirements: [""], benefits: [""],
  questions: [], is_published: true,
  require_resume: true, fallback_question: "",
};

export default function JobEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const editing = Boolean(id);
  const isOwner = user?.role === "owner";

  const [form, setForm] = useState(empty);
  const [jobMeta, setJobMeta] = useState({ priority: 0, tier: "free" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) {
      // Default company to recruiter's company_name on new jobs
      if (user?.company_name) setForm((f) => ({ ...f, company: user.company_name }));
      return;
    }
    api.get(`/jobs/${id}`).then((r) => {
      const j = r.data;
      setForm({
        ...empty, ...j,
        responsibilities: j.responsibilities?.length ? j.responsibilities : [""],
        requirements: j.requirements?.length ? j.requirements : [""],
        benefits: j.benefits?.length ? j.benefits : [""],
        questions: (j.questions || []).map((q) => ({ ...q, _uid: q.id || `q-${Math.random().toString(36).slice(2, 9)}` })),
      });
      setJobMeta({ priority: j.priority || 0, tier: j.tier || "free" });
    });
  }, [id, editing, user?.company_name]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setList = (k, idx, v) => set(k, form[k].map((x, i) => (i === idx ? v : x)));
  const addList = (k) => set(k, [...form[k], ""]);
  const rmList = (k, idx) => set(k, form[k].filter((_, i) => i !== idx));
  const moveList = (k, idx, dir) => {
    const arr = [...form[k]];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    set(k, arr);
  };

  const addQ = () => set("questions", [...form.questions, { _uid: `q-${Math.random().toString(36).slice(2, 9)}`, id: "", label: "", type: "text", required: false, options: [], _optionsRaw: "" }]);
  const setQ = (idx, patch) => set("questions", form.questions.map((q, i) => i === idx ? { ...q, ...patch } : q));
  const rmQ = (idx) => set("questions", form.questions.filter((_, i) => i !== idx));
  const moveQ = (idx, dir) => {
    const arr = [...form.questions];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    set("questions", arr);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = {
        ...form,
        responsibilities: form.responsibilities.filter((s) => s.trim()),
        requirements: form.requirements.filter((s) => s.trim()),
        benefits: form.benefits.filter((s) => s.trim()),
        // Parse any in-flight raw text into options just before saving.
        questions: form.questions.filter((q) => q.label.trim()).map((q) => {
          const optionsFromRaw = q._optionsRaw !== undefined
            ? q._optionsRaw.split(",").map((s) => s.trim()).filter(Boolean)
            : (q.options || []);
          return {
            id: q.id || q._uid, label: q.label, type: q.type, required: !!q.required,
            options: optionsFromRaw,
            show_if_question_id: q.show_if_question_id || null,
            show_if_value: q.show_if_value || null,
          };
        }),
      };
      let saved;
      if (editing) saved = (await api.put(`/jobs/${id}`, payload)).data;
      else saved = (await api.post("/jobs", payload)).data;

      if (isOwner) {
        await api.put(`/admin/jobs/${saved.job_id}/prioritise`, {
          priority: Number(jobMeta.priority) || 0,
          tier: jobMeta.tier || "free",
        });
      }
      navigate(isOwner ? "/admin" : "/recruiter");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
      <Link to={isOwner ? "/admin" : "/recruiter"} className="inline-flex items-center gap-1 text-sm font-semibold hover:text-[var(--brand-primary)]">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>
      <h1 className="font-display font-black text-4xl mt-4 tracking-tight">{editing ? "Edit role" : "Post a new role"}</h1>

      <form onSubmit={submit} className="mt-8 space-y-8">
        {/* Basics */}
        <section className="border border-[#E2E0D8] p-6 space-y-4">
          <div className="label-eyebrow">Role information</div>

          <Field label="Company *" testid="field-company">
            <input required value={form.company} onChange={(e) => set("company", e.target.value)} className={inputCls} placeholder="Acme Marine Ltd" />
          </Field>

          <Field label="Title *" testid="field-title"><input required value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Location *" testid="field-location"><input required value={form.location} onChange={(e) => set("location", e.target.value)} className={inputCls} /></Field>
            <Field label="Employment type" testid="field-type">
              <select value={form.employment_type} onChange={(e) => set("employment_type", e.target.value)} className={inputCls}>
                {["Full-time","Part-time","Contract","Internship","Freelance"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Department" testid="field-department">
              <input value={form.department} onChange={(e) => set("department", e.target.value)} placeholder="Engineering · Marketing · Operations" className={inputCls} />
            </Field>
            <Field label="Volunteer role" testid="field-volunteer">
              <label className="inline-flex items-center gap-2 h-[42px]">
                <input data-testid="field-is-volunteer" type="checkbox" checked={!!form.is_volunteer} onChange={(e) => set("is_volunteer", e.target.checked)} />
                <span className="text-sm">This is an unpaid / volunteer position</span>
              </label>
            </Field>
          </div>
          <Field label="Salary range" testid="field-salary"><input value={form.salary_range} onChange={(e) => set("salary_range", e.target.value)} placeholder="£65,000 – £85,000" className={inputCls} disabled={form.is_volunteer} /></Field>
          <Field label="Description *" testid="field-description"><textarea required rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} /></Field>
        </section>

        {/* Lists */}
        {["responsibilities", "requirements", "benefits"].map((k) => (
          <section key={k} className="border border-[#E2E0D8] p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="label-eyebrow capitalize">{k}</div>
              <button type="button" data-testid={`add-${k}`} onClick={() => addList(k)} className="text-sm font-semibold inline-flex items-center gap-1 hover:text-[var(--brand-primary)]"><Plus className="w-3 h-3" /> add</button>
            </div>
            <div className="space-y-2">
              {form[k].map((val, i) => (
                <div key={i} className="flex gap-2">
                  <div className="flex flex-col">
                    <button type="button" onClick={() => moveList(k, i, -1)} disabled={i === 0} data-testid={`${k}-up-${i}`} className="px-1 hover:text-[var(--brand-primary)] disabled:opacity-30"><ArrowUp className="w-3 h-3" /></button>
                    <button type="button" onClick={() => moveList(k, i, 1)} disabled={i === form[k].length - 1} data-testid={`${k}-down-${i}`} className="px-1 hover:text-[var(--brand-primary)] disabled:opacity-30"><ArrowDown className="w-3 h-3" /></button>
                  </div>
                  <input value={val} onChange={(e) => setList(k, i, e.target.value)} className={inputCls} placeholder={`Item ${i + 1}`} />
                  <button type="button" onClick={() => rmList(k, i)} className="px-2 hover:text-[var(--brand-primary)]"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </section>
        ))}

        {/* Questions */}
        <section className="border border-[#0F0F0F] p-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-eyebrow">Custom application questions</div>
              <p className="text-sm text-[#5C5C5C] mt-1">Anything beyond the resume.</p>
            </div>
            <button type="button" data-testid="add-question" onClick={addQ} className="btn-ink text-sm"><Plus className="w-3.5 h-3.5" /> Add question</button>
          </div>

          <div className="space-y-3">
            {form.questions.length === 0 && (
              <div className="text-sm text-[#5C5C5C] italic">No custom questions yet.</div>
            )}
            {form.questions.map((q, i) => (
              <div key={q._uid || `q-${i}`} data-testid={`question-${i}`} className="border border-[#E2E0D8] p-4 bg-[#F3F2EB]/40">
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center gap-1 mt-1">
                    <button type="button" onClick={() => moveQ(i, -1)} disabled={i === 0} data-testid={`question-${i}-up`} className="px-1 hover:text-[var(--brand-primary)] disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
                    <GripVertical className="w-4 h-4 text-[#5C5C5C]" />
                    <button type="button" onClick={() => moveQ(i, 1)} disabled={i === form.questions.length - 1} data-testid={`question-${i}-down`} className="px-1 hover:text-[var(--brand-primary)] disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="flex-1 grid gap-3">
                    <input value={q.label} onChange={(e) => setQ(i, { label: e.target.value })}
                      placeholder="Question label" className={inputCls} data-testid={`question-${i}-label`} />
                    <div className="grid grid-cols-3 gap-3 items-center">
                      <select value={q.type} onChange={(e) => setQ(i, { type: e.target.value })} className={inputCls} data-testid={`question-${i}-type`}>
                        {QTYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!q.required} onChange={(e) => setQ(i, { required: e.target.checked })} data-testid={`question-${i}-required`} />
                        Required
                      </label>
                      <button type="button" onClick={() => rmQ(i)} className="text-sm hover:text-[var(--brand-primary)] justify-self-end inline-flex items-center gap-1" data-testid={`question-${i}-delete`}>
                        <Trash2 className="w-3.5 h-3.5" /> remove
                      </button>
                    </div>
                    {q.type === "select" && (
                      <>
                        <input
                          value={q._optionsRaw !== undefined ? q._optionsRaw : (q.options || []).join(", ")}
                          onChange={(e) => setQ(i, { _optionsRaw: e.target.value })}
                          onBlur={(e) => setQ(i, {
                            _optionsRaw: e.target.value,
                            options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                          })}
                          placeholder="Option 1, Option 2, Option 3"
                          className={inputCls}
                          data-testid={`question-${i}-options`}
                        />
                        <p className="text-xs text-[#5C5C5C]">Separate choices with commas. Type freely — options parse on save.</p>
                      </>
                    )}
                    {/* Conditional: show only when a previous question's answer matches */}
                    {i > 0 && (
                      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-[#E2E0D8]">
                        <select
                          value={q.show_if_question_id || ""}
                          onChange={(e) => setQ(i, { show_if_question_id: e.target.value })}
                          data-testid={`question-${i}-show-if-id`}
                          className={inputCls}
                        >
                          <option value="">Always show this question</option>
                          {form.questions.slice(0, i).filter((p) => p.label.trim()).map((p, pi) => (
                            <option key={pi} value={p.id || p._uid}>Show only if Q{pi + 1}: {p.label.slice(0, 30)}</option>
                          ))}
                        </select>
                        <input
                          value={q.show_if_value || ""}
                          onChange={(e) => setQ(i, { show_if_value: e.target.value })}
                          disabled={!q.show_if_question_id}
                          placeholder="...equals (e.g. Yes)"
                          className={inputCls + " disabled:opacity-50"}
                          data-testid={`question-${i}-show-if-value`}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Resume / CV requirement */}
        <section className="border border-[#E2E0D8] p-6">
          <div className="label-eyebrow flex items-center gap-1.5"><FileX className="w-3 h-3 text-[var(--brand-primary)]" /> Resume / CV</div>
          <label className="flex items-center gap-2 mt-3 text-sm">
            <input type="checkbox" checked={!!form.require_resume} onChange={(e) => set("require_resume", e.target.checked)} data-testid="field-require-resume" />
            Require a CV / resume from applicants
          </label>
          {!form.require_resume && (
            <div className="mt-3">
              <label className="label-eyebrow block mb-1.5">Fallback question shown when there&apos;s no resume (optional)</label>
              <input value={form.fallback_question || ""} onChange={(e) => set("fallback_question", e.target.value)} className={inputCls} data-testid="field-fallback-question" placeholder="e.g. Why are you a great fit for this role?" />
              <p className="text-xs text-[#5C5C5C] mt-1.5">Only used when there are no custom questions either — gives applicants something to write about.</p>
            </div>
          )}
        </section>

        {/* Owner-only: priority + tier */}
        {isOwner && (
          <section className="border border-[#0F0F0F] p-6 bg-[#0F0F0F]/[0.02]">
            <div className="label-eyebrow flex items-center gap-1.5"><Star className="w-3 h-3 text-[var(--brand-primary)]" /> Owner — Listing priority</div>
            <p className="text-sm text-[#5C5C5C] mt-1">Owner roles can be promoted above paid tiers. Tier sets the badge; priority breaks ties.</p>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <Field label="Tier" testid="field-tier">
                <select value={jobMeta.tier} onChange={(e) => setJobMeta((m) => ({ ...m, tier: e.target.value }))} className={inputCls}>
                  <option value="free">Standard</option>
                  <option value="featured">Featured</option>
                  <option value="premium">Premium Spotlight</option>
                </select>
              </Field>
              <Field label="Priority (0–1000)" testid="field-priority">
                <input type="number" min={0} max={1000} value={jobMeta.priority}
                  onChange={(e) => setJobMeta((m) => ({ ...m, priority: e.target.value }))} className={inputCls} />
              </Field>
            </div>
          </section>
        )}

        <label className="flex items-center gap-2">
          <input type="checkbox" checked={form.is_published} onChange={(e) => set("is_published", e.target.checked)} data-testid="field-published" />
          Publish immediately (uncheck to save as draft — won&apos;t appear publicly)
        </label>
        {!form.is_published && (
          <div data-testid="draft-badge" className="text-xs uppercase tracking-widest font-bold bg-amber-100 text-amber-900 px-2 py-1 inline-block">
            Will save as DRAFT — only visible to you
          </div>
        )}

        {error && <div data-testid="job-error" className="text-sm text-[var(--brand-primary)] font-medium">{error}</div>}

        <div className="flex gap-3">
          <button data-testid="save-job" type="submit" disabled={saving} className="btn-terra text-base">{saving ? "Saving…" : (editing ? "Save changes" : "Publish role")}</button>
          <Link to={isOwner ? "/admin" : "/recruiter"} className="btn-ghost text-base">Cancel</Link>
        </div>
      </form>
    </div>
  );
}

const inputCls = "w-full border border-[#0F0F0F] px-3 py-2 rounded-sm bg-transparent text-base";
function Field({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <label className="label-eyebrow block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
